/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { routeTable } from 'src/core/utils/routeTable';

const messages = defineMessages({
    failedTitle: {
        id: 'qnaMaker.useInitializeQnaMakerProject.failedTitle',
        defaultMessage: 'Failed to open project'
    },
    serverErrorFailedContent: {
        id: 'qnaMaker.useInitializeQnaMakerProject.serverErrorFailedContent',
        defaultMessage: 'A server error occurred while trying to open your project. Please refresh and try again.'
    },
    differentResourcesFailedContent: {
        id: 'qnaMaker.useInitializeQnaMakerProject.differentResourcesFailedContent',
        defaultMessage: 'The project doesn\'t exist under the selected azure resource, please select another azure resource.'
    },
    loadingProjectTitle: {
        id: 'qnaMaker.useInitializeQnaMakerProject.loadingProjectTitle',
        defaultMessage: 'Loading the project...'
    },
    loadingProjectContent: {
        id: 'qnaMaker.useInitializeQnaMakerProject.loadingProjectContent',
        defaultMessage: 'This operation might take several minutes.'
    },
    loadingProjectCompletedTitle: {
        id: 'qnaMaker.useInitializeQnaMakerProject.loadingProjectCompletedTitle',
        defaultMessage: 'Loading project completed'
    },
    loadingProjectCompletedContent: {
        id: 'qnaMaker.useInitializeQnaMakerProject.loadingProjectCompletedContent',
        defaultMessage: 'Project loaded successfully'
    }
});
/**
 * A hook used to initialize a Qna Maker project by doing the following.
 * 1) Fetches the current project if it's not already fetched.
 * 2) Sets the initialized state to be true.
 */
export const useInitializeQnaMakerProject = ({ projectId, csAccountPath }: { projectId: string; csAccountPath: string }) => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const projectTracker = useProgressTracker({ persistOnComplete: true });

    const { azureStore } = rootStore;
    const currentProject = rootStore.currentQnaMakerProjectStore;
    const currentSelectedCsStore = azureStore.session.csAccountStore;
    const failedToastContent = csAccountPath && csAccountPath !== rootStore.azureStore.session.csAccountStore?.path
            ? messages.differentResourcesFailedContent
            : messages.serverErrorFailedContent;

    React.useEffect(() => {
        if (projectTracker.isNotStarted) {
            dispatcher.dispatch('openQnaMakerProject', {
                projectId,
                csAccountPath,
                handleDifferentResources: () => {
                    dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true });
                },
                tracker: projectTracker,
                toastData: {
                    failed: {
                        title: localize(messages.failedTitle),
                        content: localize(failedToastContent)
                    },
                    running: { title: localize(messages.loadingProjectTitle), content: localize(messages.loadingProjectContent) },
                    completed: {
                        title: localize(messages.loadingProjectCompletedTitle),
                        content: localize(messages.loadingProjectCompletedContent)
                    }
                }
            });
        }
    }, [currentProject, projectTracker.state]);

    React.useEffect(() => {
        if (currentProject?.csAccountPath && currentProject?.csAccountPath !== currentSelectedCsStore?.path) {
            history.push(routeTable.qnaMaker.root());
        }
    }, [currentSelectedCsStore, currentProject]);

    React.useEffect(() => () => dispatcher.dispatch('closeQnaMakerProject'), []);

    return { isLoaded: projectTracker.isCompleted, isFailed: projectTracker.isFailed };
};
